// ============================================================================
// FUNÇÕES UTILITÁRIAS DE COR
// ============================================================================

/**
 * Ajusta o brilho de uma cor hexadecimal
 */
export const adjustColorBrightness = (hex: string, percent: number): string => {
  hex = hex.replace('#', '');
  
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  r = Math.min(255, Math.max(0, Math.round(r + (r * percent / 100))));
  g = Math.min(255, Math.max(0, Math.round(g + (g * percent / 100))));
  b = Math.min(255, Math.max(0, Math.round(b + (b * percent / 100))));
  
  const rr = r.toString(16).padStart(2, '0');
  const gg = g.toString(16).padStart(2, '0');
  const bb = b.toString(16).padStart(2, '0');
  
  return `#${rr}${gg}${bb}`;
};

/**
 * Detecta se uma cor é escura (usa fórmula de luminância)
 */
export const isColorDark = (hex: string): boolean => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.6;
};

/**
 * Converte HEX para RGB
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Converte RGB para HSL
 */
export const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converte HSL para RGB
 */
export const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

/**
 * Converte RGB para HEX
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Cria variação pastel de uma cor
 */
export const createPastelVariation = (hex: string, lightness: number = 88, saturation: number = 35): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const newRgb = hslToRgb(hsl.h, saturation, lightness);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
};

/**
 * Obtém borda dessaturada
 */
export const getDesaturatedBorder = (bgColor: string): string => {
  const rgb = hexToRgb(bgColor);
  if (!rgb) return '#d1d5db';
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const newS = Math.max(0, hsl.s - 20);
  const newL = Math.max(0, hsl.l - 15);
  const borderRgb = hslToRgb(hsl.h, newS, newL);
  return rgbToHex(borderRgb.r, borderRgb.g, borderRgb.b);
};

/**
 * Obtém cor de texto legível baseada no fundo
 */
export const getReadableTextColor = (hexBg: string): string => {
  const rgb = hexToRgb(hexBg);
  if (!rgb) return '#000000';
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 155 ? '#000000' : '#ffffff';
};

/**
 * Dessatura uma cor
 */
export const desaturateColor = (hex: string, desaturationPercent: number = 40): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const newS = Math.max(0, hsl.s - desaturationPercent);
  const newRgb = hslToRgb(hsl.h, newS, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
};

/**
 * Aumenta o contraste de uma cor
 */
export const increaseContrast = (hex: string, contrastPercent: number = 20): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const newL = hsl.l > 50 ? Math.min(100, hsl.l + contrastPercent) : Math.max(0, hsl.l - contrastPercent);
  const newRgb = hslToRgb(hsl.h, hsl.s, newL);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
};

/**
 * Converte para tom de post-it
 */
export const convertToPostItTone = (hex: string): { bg: string; border: string; footer: string } => {
  const rgb = hexToRgb(hex);
  if (!rgb) return { bg: '#fef9c3', border: '#fde047', footer: '#fef08a' };
  
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const bgRgb = hslToRgb(hsl.h, 100, 88);
  const borderRgb = hslToRgb(hsl.h, 64, 70);
  const footerRgb = hslToRgb(hsl.h, 100, 81);
  
  return {
    bg: rgbToHex(bgRgb.r, bgRgb.g, bgRgb.b),
    border: rgbToHex(borderRgb.r, borderRgb.g, borderRgb.b),
    footer: rgbToHex(footerRgb.r, footerRgb.g, footerRgb.b)
  };
};
